const DB_NAME = "bb-driver-cache-v1";
const DB_VERSION = 1;
const MANIFEST_STORE = "manifest";
const PENDING_STORE = "pending_stamps";

import type { DriverManifest, PendingDriverStamp } from "@/lib/logistics/driverTypes";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(MANIFEST_STORE)) {
        db.createObjectStore(MANIFEST_STORE);
      }
      if (!db.objectStoreNames.contains(PENDING_STORE)) {
        db.createObjectStore(PENDING_STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export async function readCachedDriverManifest(): Promise<DriverManifest | null> {
  if (typeof indexedDB === "undefined") return null;
  const db = await openDb();
  const tx = db.transaction(MANIFEST_STORE, "readonly");
  const store = tx.objectStore(MANIFEST_STORE);
  const manifest = await new Promise<DriverManifest | null>((resolve, reject) => {
    const req = store.get("current");
    req.onsuccess = () => resolve((req.result as DriverManifest | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return manifest;
}

export async function writeCachedDriverManifest(manifest: DriverManifest): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const db = await openDb();
  const tx = db.transaction(MANIFEST_STORE, "readwrite");
  tx.objectStore(MANIFEST_STORE).put(manifest, "current");
  await txDone(tx);
  db.close();
}

export async function listPendingDriverStamps(): Promise<PendingDriverStamp[]> {
  if (typeof indexedDB === "undefined") return [];
  const db = await openDb();
  const tx = db.transaction(PENDING_STORE, "readonly");
  const items = await new Promise<PendingDriverStamp[]>((resolve, reject) => {
    const req = tx.objectStore(PENDING_STORE).getAll();
    req.onsuccess = () => resolve((req.result as PendingDriverStamp[]) ?? []);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return items.sort((a, b) => a.queued_at.localeCompare(b.queued_at));
}

export async function enqueuePendingDriverStamp(
  item: Omit<PendingDriverStamp, "id" | "queued_at"> & { id?: string; queued_at?: string }
): Promise<PendingDriverStamp> {
  const row: PendingDriverStamp = {
    id: item.id ?? crypto.randomUUID(),
    stop_id: item.stop_id,
    field: item.field,
    value: item.value,
    queued_at: item.queued_at ?? new Date().toISOString(),
  };
  if (typeof indexedDB === "undefined") return row;
  const db = await openDb();
  const tx = db.transaction(PENDING_STORE, "readwrite");
  tx.objectStore(PENDING_STORE).put(row);
  await txDone(tx);
  db.close();
  return row;
}

export async function removePendingDriverStamp(id: string): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const db = await openDb();
  const tx = db.transaction(PENDING_STORE, "readwrite");
  tx.objectStore(PENDING_STORE).delete(id);
  await txDone(tx);
  db.close();
}

export function applyStampToManifest(
  manifest: DriverManifest,
  stopId: string,
  field: PendingDriverStamp["field"],
  value: string
): DriverManifest {
  return {
    ...manifest,
    routes: manifest.routes.map((route) => ({
      ...route,
      stops: route.stops.map((stop) =>
        stop.id === stopId ? { ...stop, [field]: value } : stop
      ),
    })),
  };
}
